import { apiHelpers } from '@/services/api';

// ==============================================
// Types
// ==============================================

export type InvitationStatus = 'PENDING' | 'ACCEPTED' | 'DECLINED' | 'MAYBE' | 'EXPIRED';

export interface UserProfile {
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
}

export interface UserWithProfile {
  id: string;
  email: string;
  profile: UserProfile | null;
}

export interface EventSummary {
  id: string;
  title: string;
  description: string | null;
  startTime: string;
  endTime: string;
  location: string | null;
  category: string;
  creator: UserWithProfile;
}

export interface EventInvitation {
  id: string;
  eventId: string;
  invitedById: string;
  invitedUserId: string;
  status: InvitationStatus;
  message: string | null;
  respondedAt: string | null;
  expiresAt: string | null;
  createdAt: string;
  updatedAt: string;
  event: EventSummary;
  invitedBy: UserWithProfile;
  invitedUser: UserWithProfile;
}

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

export interface PaginatedInvitationsResponse {
  invitations: EventInvitation[];
  meta: PaginationMeta;
}

export interface InvitationFilters {
  page?: number;
  limit?: number;
  status?: InvitationStatus;
  eventId?: string;
}

export interface CreateInvitationInput {
  userIds: string[];
  message?: string | null;
  expiresAt?: string | null;
}

export interface RespondInvitationInput {
  status: 'ACCEPTED' | 'DECLINED' | 'MAYBE';
}

export interface UpdateInvitationInput {
  message?: string | null;
  expiresAt?: string | null;
}

// ==============================================
// Event Invitation Service
// ==============================================

export const eventInvitationService = {
  // ==============================================
  // Event Creator Routes (on events)
  // ==============================================

  /**
   * Create invitations for an event
   */
  async createInvitations(
    eventId: string,
    data: CreateInvitationInput
  ): Promise<EventInvitation[]> {
    return apiHelpers.post<EventInvitation[]>(`/events/${eventId}/invitations`, data);
  },

  /**
   * Get invitations for an event (creator only)
   */
  async getEventInvitations(
    eventId: string,
    filters?: InvitationFilters
  ): Promise<PaginatedInvitationsResponse> {
    const params: Record<string, unknown> = {};

    if (filters) {
      if (filters.page) params.page = filters.page;
      if (filters.limit) params.limit = filters.limit;
      if (filters.status) params.status = filters.status;
    }

    return apiHelpers.get<PaginatedInvitationsResponse>(
      `/events/${eventId}/invitations`,
      params
    );
  },

  /**
   * Update an invitation
   */
  async updateInvitation(
    eventId: string,
    invitationId: string,
    data: UpdateInvitationInput
  ): Promise<EventInvitation> {
    return apiHelpers.patch<EventInvitation>(
      `/events/${eventId}/invitations/${invitationId}`,
      data
    );
  },

  /**
   * Cancel/delete an invitation
   */
  async deleteInvitation(eventId: string, invitationId: string): Promise<void> {
    return apiHelpers.delete(`/events/${eventId}/invitations/${invitationId}`);
  },

  // ==============================================
  // User Routes (for received invitations)
  // ==============================================

  /**
   * Get my received invitations
   */
  async getMyInvitations(
    filters?: InvitationFilters
  ): Promise<PaginatedInvitationsResponse> {
    const params: Record<string, unknown> = {};

    if (filters) {
      if (filters.page) params.page = filters.page;
      if (filters.limit) params.limit = filters.limit;
      if (filters.status) params.status = filters.status;
      if (filters.eventId) params.eventId = filters.eventId;
    }

    return apiHelpers.get<PaginatedInvitationsResponse>(
      '/event-invitations/my',
      params
    );
  },

  /**
   * Get pending invitation count
   */
  async getPendingInvitationCount(): Promise<{ count: number }> {
    return apiHelpers.get<{ count: number }>('/event-invitations/my/count');
  },

  /**
   * Get a single invitation by ID
   */
  async getInvitationById(invitationId: string): Promise<EventInvitation> {
    return apiHelpers.get<EventInvitation>(`/event-invitations/${invitationId}`);
  },

  /**
   * Respond to an invitation
   */
  async respondToInvitation(
    invitationId: string,
    data: RespondInvitationInput
  ): Promise<EventInvitation> {
    return apiHelpers.post<EventInvitation>(
      `/event-invitations/${invitationId}/respond`,
      data
    );
  },
};

export default eventInvitationService;
